import { z } from 'zod';
import { isValidCPF } from '@/shared/utils/cpfs';
import { normalizePersonFormFromApi, profileHasPresence } from '@/modules/people/presentation/person-form.utils';
import { telefoneFormSchema } from '@/shared/utils/formZod.schema.utils';

export type PersonTipo = 'todos' | 'defensor' | 'estagiario' | 'colaborador' | 'usuario';

/** Espelha o que o formulário envia em `edit.tsx` (perfis ocultos não entram no payload). */
export interface PersonFormValidationContext {
  tipo: PersonTipo;
  podeVerUsuarios: boolean;
  podeVerColaborador: boolean;
}

/** API pode retornar `null` em edição; normaliza para string antes da validação. */
function nullToEmpty(val: unknown): string {
  return val == null ? '' : String(val);
}

const optionalNullableStringSchema = z.preprocess(nullToEmpty, z.string().optional());

const optionalBooleanSchema = z.preprocess(
  (val) => (val == null ? undefined : val),
  z.boolean().optional(),
);

const optionalReferenceIdSchema = z.preprocess(
  (val) => (val === '' || val == null ? undefined : Number(val)),
  z.number().optional(),
);

const optionalEmailSchema = z.preprocess(
  nullToEmpty,
  z
    .string()
    .trim()
    .refine((v) => !v || z.string().email().safeParse(v).success, 'E-mail inválido'),
);

/** Obrigatório no cadastro (campo com * na aba Dados Pessoais). */
const requiredEmailSchema = z.preprocess(
  nullToEmpty,
  z
    .string()
    .trim()
    .min(1, 'Informe o e-mail institucional')
    .email('E-mail institucional inválido'),
);

/** Perfil de usuário entra no escopo da validação (existe no banco ou está ativo). */
function shouldValidateUsuario(
  ctx: PersonFormValidationContext,
  usuario?: { id?: number; ativo?: boolean },
): boolean {
  if (!ctx.podeVerUsuarios) return false;
  if (ctx.tipo !== 'usuario' && ctx.tipo !== 'todos') return false;
  return profileHasPresence(usuario);
}

/** Perfil de colaborador entra no escopo da validação (existe no banco ou está ativo). */
function shouldValidateColaborador(
  ctx: PersonFormValidationContext,
  colaborador?: { id?: number; ativo?: boolean },
): boolean {
  if (!ctx.podeVerColaborador) return false;
  if (ctx.tipo !== 'colaborador' && ctx.tipo !== 'todos') return false;
  return profileHasPresence(colaborador);
}

function isTipoVinculoInformado(tipo: unknown): boolean {
  if (tipo === '' || tipo === null || tipo === undefined) return false;
  const n = Number(tipo);
  return !Number.isNaN(n) && n > 0;
}

function validateActiveUsuario(
  data: { usuario?: { ativo?: boolean; id?: number; username?: string; senha?: string; papel?: { id?: unknown } } },
  ctx: z.RefinementCtx,
) {
  if (!data.usuario?.ativo) return;
  if (!data.usuario.username?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Informe o login', path: ['usuario', 'username'] });
  }
  if (!data.usuario.id && !String(data.usuario.senha ?? '').trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Informe a senha', path: ['usuario', 'senha'] });
  }
  const papelId = Number(data.usuario.papel?.id);
  if (!Number.isInteger(papelId) || papelId <= 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Selecione o papel', path: ['usuario', 'papel', 'id'] });
  }
}

function validateActiveColaborador(
  data: { colaborador?: { ativo?: boolean; tipo_vinculo?: unknown } },
  ctx: z.RefinementCtx,
) {
  if (!data.colaborador?.ativo) return;
  if (!isTipoVinculoInformado(data.colaborador.tipo_vinculo)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Selecione o tipo de vínculo',
      path: ['colaborador', 'tipo_vinculo'],
    });
  }
}

const personFormBaseSchema = z
  .object({
    nome: z.string().trim().min(1, 'Informe o nome'),
    sobrenome: z.string().trim().nullish(),
    cpf: z
      .string()
      .trim()
      .min(1, 'Informe o CPF')
      .refine((v) => isValidCPF(v), 'CPF inválido'),
    dataNascimento: optionalNullableStringSchema,
    estadocivil: z.coerce.number().optional(),
    email: optionalEmailSchema,
    emailInstitucional: requiredEmailSchema,
    podeserresponsavel: optionalBooleanSchema,
    endereco: z
      .object({
        cep: optionalNullableStringSchema,
        logradouro: optionalNullableStringSchema,
        numero: z.union([z.string(), z.number()]).optional(),
        complemento: optionalNullableStringSchema,
        bairro: optionalNullableStringSchema,
        cidade: optionalNullableStringSchema,
        estado: optionalNullableStringSchema,
      })
      .optional(),
    telefone: telefoneFormSchema,
    celular: telefoneFormSchema,
    usuario: z
      .object({
        id: z.coerce.number().optional(),
        ativo: optionalBooleanSchema,
        username: optionalNullableStringSchema,
        senha: optionalNullableStringSchema,
        funcao: optionalNullableStringSchema,
        papel: z.object({ id: z.union([z.string(), z.number()]).optional() }).optional(),
        corfundotabela: optionalNullableStringSchema,
        corletratabela: optionalNullableStringSchema,
        recebenotificacoes: optionalBooleanSchema,
      })
      .passthrough()
      .optional(),
    colaborador: z
      .object({
        id: z.coerce.number().optional(),
        ativo: optionalBooleanSchema,
        tipo_vinculo: z.union([z.string(), z.number()]).optional(),
        orgao_origem: optionalNullableStringSchema,
        vinculo_direto: optionalBooleanSchema,
        cessao_onerosa: optionalBooleanSchema,
        anotacao: optionalNullableStringSchema,
        data_cessao: optionalNullableStringSchema,
        data_saida: optionalNullableStringSchema,
        funcao: z
          .object({
            id: optionalReferenceIdSchema,
          })
          .optional(),
      })
      .passthrough()
      .optional(),
    defensor: z
      .object({
        id: z.coerce.number().optional(),
        ativo: optionalBooleanSchema,
      })
      .passthrough()
      .optional(),
    estagiario: z
      .object({
        id: z.coerce.number().optional(),
        ativo: optionalBooleanSchema,
        anotacao: optionalNullableStringSchema,
      })
      /** Preserva `estagios` e demais campos enviados ao salvar. */
      .passthrough()
      .optional(),
  })
  .passthrough();

/** Validação alinhada ao cadastro: mesmos campos * e perfis enviados conforme `tipo`/permissões. */
export function buildPersonFormSchema(context: PersonFormValidationContext) {
  const validated = personFormBaseSchema.superRefine((data, ctx) => {
    const usuario = data.usuario ?? undefined;
    const colaborador = data.colaborador ?? undefined;
    // Campos obrigatórios do perfil só quando ativo; inativo com `id` permanece no formulário sem exigir login/papel/vínculo.
    if (shouldValidateUsuario(context, usuario) && usuario?.ativo) {
      validateActiveUsuario({ ...data, usuario }, ctx);
    }
    if (shouldValidateColaborador(context, colaborador) && colaborador?.ativo) {
      validateActiveColaborador({ ...data, colaborador }, ctx);
    }
  });

  return z.preprocess(
    (val) => (val != null && typeof val === 'object' ? normalizePersonFormFromApi(val as never) : val),
    validated,
  );
}

export const personFormSchema = buildPersonFormSchema({
  tipo: 'todos',
  podeVerUsuarios: true,
  podeVerColaborador: true,
});

export type PersonFormValues = z.infer<typeof personFormBaseSchema>;
